import { Injectable } from "@angular/core";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ProgressCategory, ProgressCategoryBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem, ProgressItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressItemStatus, ProgressItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/progress-item-status";
import { ProgressProduct, ProgressProductBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Partner } from "@common/ADAPT.Common.Model/partner/partner";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IDxCustomRuleValidationCallbackEvent } from "@common/ux/dx.types";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleEntity, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { catchError, concatMap, defer, forkJoin, from, lastValueFrom, of, tap, throwError, toArray } from "rxjs";
import { map, switchMap } from "rxjs/operators";

export type IProgressItemImportSource = Pick<ProgressItem, "name" | "ordinal">;

export interface IProgressCategoryImportSource extends Pick<ProgressCategory, "name" | "ordinal"> {
    progressItems: IProgressItemImportSource[];
}

export interface IProgressProductImportSource extends Pick<ProgressProduct, "progressProductId" | "organisationId" | "partnerId" | "name" | "code" | "ordinal"> {
    organisationName?: string;
    organisationUrlIdentifier?: string;
    progressCategories: IProgressCategoryImportSource[];
}

@Injectable({
    providedIn: "root",
})
export class ProgressService {
    constructor(
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
    ) {}

    public static scrollToNewProduct(container: HTMLElement | undefined, progressProductId: number) {
        this.scrollToNewEntity(container, `[data-progress-product-id="${progressProductId}"]`);
    }

    public static scrollToNewCategory(container: HTMLElement | undefined, progressCategoryId: number) {
        this.scrollToNewEntity(container, `[data-progress-category-id="${progressCategoryId}"]`);
    }

    public static getTooltipTextForItems(items: ProgressItem[]) {
        const messages: string[] = [];

        const itemCount = items.length;
        if (itemCount === 0) {
            return `No tasks have been defined yet.`;
        }

        const statuses = [ProgressItemStatus.Done, ProgressItemStatus.InProgress, ProgressItemStatus.ToDo];
        for (const status of statuses) {
            const meta = ProgressItemStatusMetadata.ByStatus[status];
            const count = items.filter((item) => item.status === status).length;
            const icon = `<i class="fas fa-circle me-1" style="color: ${meta.colour}"></i>`;
            messages.push(`${icon} ${meta.name} ${count}/${itemCount} - ${Math.round((count / itemCount) * 100)}%`);
        }

        return `<div class="text-start">${messages.join("<br>")}</div>`;
    }

    public static getProgressPercent(items: ProgressItem[]) {
        const itemCount = items.length;
        if (itemCount === 0) {
            return 0;
        }

        const doneItems = items.filter((item) => item.status === ProgressItemStatus.Done).length;
        return Math.round((doneItems / itemCount) * 100);
    }

    public static importSourcesConflict(source: Pick<ProgressProduct, "name">, other: Pick<ProgressProduct, "name">) {
        return ProgressService.getImportNameKey(source) === ProgressService.getImportNameKey(other);
    }

    public static getImportNameKey(product: Pick<ProgressProduct, "name">) {
        return product.name.trim().toLowerCase();
    }

    private static scrollToNewEntity(container: HTMLElement | undefined, selector: string) {
        setTimeout(() => {
            const element = container?.querySelector(selector);
            element?.querySelector(".card-header")?.scrollIntoView({ behavior: "smooth", block: "center" });
            element?.classList.add("highlight");
            setTimeout(() => element?.classList.remove("highlight"), 2500);
        });
    }

    public getTemplateProgressProducts() {
        return this.getProgressProducts(undefined, true);
    }

    public getProgressProducts(organisationId?: number, forceRemote = false) {
        const predicate = new MethodologyPredicate<ProgressProduct>("organisationId", "==", organisationId ?? null);
        return this.commonDataService.getByPredicate(ProgressProductBreezeModel, predicate, forceRemote);
    }

    public getImportableProgressProducts() {
        return defer(async () => {
            const transientData = await this.commonDataService.promiseToCreateTransientCommonDataInstance();
            try {
                const products = await lastValueFrom(transientData.getWithOptions(ProgressProductBreezeModel, "progressImportSources", {
                    params: [],
                    namedParams: { organisationId: 0, allowStakeholderOverride: true },
                    forceRemote: true,
                    skipLocalOrganisationIdCheck: true,
                    navProperty: "organisation,progressCategories.progressItems",
                }));
                // Keep plain definitions: transient cleanup detaches entities and clears their navigation properties.
                return products.filter((product) => product.progressCategories.length > 0).map((product): IProgressProductImportSource => ({
                    progressProductId: product.progressProductId,
                    organisationId: product.organisationId,
                    organisationName: product.organisation?.name,
                    organisationUrlIdentifier: product.organisation?.urlIdentifier,
                    partnerId: product.partnerId,
                    name: product.name,
                    code: product.code,
                    ordinal: product.ordinal,
                    progressCategories: product.progressCategories.map((category) => ({
                        name: category.name,
                        ordinal: category.ordinal,
                        progressItems: category.progressItems.map((item) => ({ name: item.name, ordinal: item.ordinal })),
                    })),
                }));
            } finally {
                await Promise.resolve(transientData.cleanupInstance?.());
            }
        });
    }

    public importProgressProducts(ids: number[], organisation: Organisation) {
        return defer(() => {
            if (this.commonDataService.hasChanges() || this.commonDataService.saveInProgress) {
                return throwError(() => new Error("Please save or discard your changes before importing."));
            }

            const created: IBreezeEntity[] = [];
            return forkJoin([
                this.getImportableProgressProducts(),
                this.getProgressProducts(organisation.organisationId, true),
            ]).pipe(
                switchMap(([sources, existing]) => {
                    const selected = sources.filter((product) => product.organisationId !== organisation.organisationId && ids.includes(product.progressProductId))
                        .sort(SortUtilities.getSortByFieldFunction<IProgressProductImportSource>("ordinal"));
                    if (!selected.length || selected.length !== new Set(ids).size) {
                        return throwError(() => new Error("Select available journey progress modules to import."));
                    }
                    if (selected.some((product, index) => [...existing, ...selected.slice(0, index)].some((other) => ProgressService.importSourcesConflict(product, other)))) {
                        return throwError(() => new Error("A selected journey progress module already exists in this organisation."));
                    }
                    const ordinal = existing.reduce((max, product) => Math.max(max, product.ordinal), -1) + 1;
                    return from(selected).pipe(
                        concatMap((product, index) => this.cloneProgressProduct(product, organisation, ordinal + index, created)),
                        toArray(),
                    );
                }),
                switchMap(() => this.commonDataService.saveEntities(created)),
                catchError((error) => this.commonDataService.rejectChanges(created).pipe(
                    switchMap(() => throwError(() => error)),
                )),
            );
        });
    }

    public cloneProgressProduct(progressProduct: IProgressProductImportSource, organisation: Organisation, ordinal: number, created: IBreezeEntity[] = []) {
        return this.commonDataService.create(ProgressProductBreezeModel, {
            organisationId: organisation.organisationId,
            name: progressProduct.name,
            ordinal,
        }).pipe(
            switchMap((newProduct) => {
                created.push(newProduct);
                if (progressProduct.progressCategories.length === 0) {
                    return of(newProduct);
                }

                return from(progressProduct.progressCategories).pipe(
                    concatMap((category) => this.cloneProgressCategory(category, newProduct, created)),
                    toArray(),
                    map(() => newProduct),
                );
            }),
        );
    }

    private cloneProgressCategory(progressCategory: IProgressCategoryImportSource, destProgressProduct: ProgressProduct, created: IBreezeEntity[]) {
        return this.commonDataService.create(ProgressCategoryBreezeModel, {
            progressProduct: destProgressProduct,
            name: progressCategory.name,
            ordinal: progressCategory.ordinal,
        }).pipe(
            switchMap((newCategory) => {
                created.push(newCategory);
                if (progressCategory.progressItems.length === 0) {
                    return of(newCategory);
                }

                return from(progressCategory.progressItems).pipe(
                    concatMap((item) => this.cloneProgressItem(item, newCategory, created)),
                    toArray(),
                    map(() => newCategory),
                );
            }),
        );
    }

    private cloneProgressItem(progressItem: IProgressItemImportSource, destProgressCategory: ProgressCategory, created: IBreezeEntity[]) {
        return this.commonDataService.create(ProgressItemBreezeModel, {
            progressCategory: destProgressCategory,
            name: progressItem.name,
            ordinal: progressItem.ordinal,
            status: ProgressItemStatus.ToDo,
        }).pipe(
            tap((item) => created.push(item)),
        );
    }
    public reorderProgressEntities<T extends ProgressProduct | ProgressCategory>(entities: T[], fromIndex: number, toIndex: number) {
        return defer(() => {
            if (this.commonDataService.hasChanges() || this.commonDataService.saveInProgress) {
                return throwError(() => new Error("Please save or discard your changes before reordering."));
            }

            SortUtilities.reorderItemInIntegerSortedArray(entities, "ordinal", fromIndex, toIndex);
            return this.commonDataService.saveEntities(entities).pipe(
                catchError((error) => this.commonDataService.rejectChanges(entities).pipe(
                    tap(() => entities.sort(SortUtilities.getSortByFieldFunction<T>("ordinal"))),
                    switchMap(() => throwError(() => error)),
                )),
            );
        });
    }

    public createNewProgressProduct(products: ProgressProduct[], organisationId?: number, partners: Partner[] = []) {
        return this.commonDataService.create(ProgressProductBreezeModel, {
            organisationId,
            ordinal: products.reduce((max, product) => Math.max(max, product.ordinal), -1) + 1,
        }).pipe(
            switchMap((progressProduct) => this.editProgressProduct(progressProduct, products, partners).pipe(
                map(() => progressProduct),
            )),
        );
    }

    public editProgressProduct(progressProduct: ProgressProduct, products: ProgressProduct[], partners: Partner[] = []) {
        const siblings = products;
        const editedEntity = progressProduct;
        const entities: IEditSimpleEntity[] = [{
            entity: progressProduct,
            fieldName: "name",
            label: "Name",
            type: SimpleValueType.Text,
            usePropertyValidator: true,
            customValidators: [{
                validator: ({ value }) => typeof value === "string" && !!value.trim(),
                message: "Enter a name",
            }, {
                validator: ({ value }) => typeof value === "string" && !siblings.some((entity) =>
                    entity !== editedEntity && entity.name.trim().toLowerCase() === value.trim().toLowerCase()),
                message: "That name already exists",
            }],
        }];

        // only when editing in nimbus since system products need a code
        if (!progressProduct.organisationId) {
            entities.push({
                entity: progressProduct,
                fieldName: "code",
                label: "Code",
                type: SimpleValueType.Text,
                usePropertyValidator: true,
                customValidators: [{
                    validator: ({ value }: IDxCustomRuleValidationCallbackEvent) => typeof value === "string"
                        ? !products.some((i) => i.code?.trim().toLowerCase() === value.trim().toLowerCase() && i !== progressProduct)
                        : false,
                    message: "A journey progress module with that code already exists",
                }, {
                    validator: ({ value }: IDxCustomRuleValidationCallbackEvent) => typeof value === "string"
                        ? !!value.trim()
                        : false,
                    message: "Enter a code for this journey progress module",
                }],
            });
        }

        if (!progressProduct.organisationId) {
            entities.push({
                entity: progressProduct,
                fieldName: "partnerId",
                label: "Partner (leave empty to make available to all coaches)",
                type: SimpleValueType.Select,
                selectOptional: true,
                selectValues: partners.map((partner) => ({ text: partner.name, value: partner.partnerId })),
            });
        }

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: (progressProduct.entityAspect.entityState.isAdded() ? "Add" : "Edit") + " journey progress module",
            entities,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData);
    }

    public createNewProgressCategory(progressProduct: ProgressProduct) {
        return this.commonDataService.create(ProgressCategoryBreezeModel, {
            progressProduct,
            ordinal: progressProduct.progressCategories.reduce((max, category) => Math.max(max, category.ordinal), -1) + 1,
        }).pipe(
            switchMap((progressCategory) => this.editProgressCategory(progressCategory).pipe(
                map(() => progressCategory),
            )),
        );
    }

    public deleteProduct(product: ProgressProduct, products: ProgressProduct[]) {
        return this.deleteProgressEntity(product, products, [
            ...product.progressCategories.flatMap((category) => category.progressItems),
            ...product.progressCategories,
        ], "journey progress module", !product.organisationId && product.code
            ? "This is a system journey progress module, please confirm you intend to delete this."
            : undefined);
    }

    public deleteCategory(category: ProgressCategory) {
        return this.deleteProgressEntity(category, category.progressProduct.progressCategories, [...category.progressItems],
            "journey progress category");
    }

    private deleteProgressEntity<T extends ProgressProduct | ProgressCategory>(entity: T, siblings: T[], descendants: IBreezeEntity[], label: string, checkboxMessage?: string) {
        // Do not race task autosaves or discard unfinished task edits.
        return defer(() => {
            if (this.commonDataService.hasChanges() || this.commonDataService.saveInProgress) {
                return this.dialogService.showMessageDialog("Unsaved changes", "Please save or discard your task changes before deleting.", "OK");
            }

            const remaining = [...siblings];
            const affected = [...descendants, ...remaining];
            const childrenLabel = entity.entityType.shortName === ProgressCategoryBreezeModel.toType
                ? "tasks"
                : "categories/tasks";
            return this.dialogService.openConfirmationDialog({
                title: `Delete ${label}`,
                message: `Delete "${entity.name}" and all its ${childrenLabel}, including recorded progress, dates and comments?`,
                confirmButtonPreset: "deleteAndSave",
                checkboxMessage,
            }).pipe(
                switchMap(() => {
                    // Mark loaded children explicitly, keeping Breeze's cache and the database in agreement.
                    for (const child of [...descendants, entity]) {
                        child.entityAspect.setDeleted();
                    }
                    for (const sibling of remaining) {
                        if (sibling !== entity && sibling.ordinal > entity.ordinal) {
                            sibling.ordinal--;
                        }
                    }
                    return this.commonDataService.saveEntities(affected);
                }),
                catchError((error) => this.commonDataService.rejectChanges(affected).pipe(
                    switchMap(() => throwError(() => error)),
                )),
            );
        });
    }

    public editProgressCategory(progressCategory: ProgressCategory) {
        const siblings = progressCategory.progressProduct.progressCategories;
        const editedEntity = progressCategory;
        const entities: IEditSimpleEntity[] = [{
            entity: progressCategory,
            fieldName: "name",
            label: "Name",
            type: SimpleValueType.Text,
            usePropertyValidator: true,
            customValidators: [{
                validator: ({ value }) => typeof value === "string" && !!value.trim(),
                message: "Enter a name",
            }, {
                validator: ({ value }) => typeof value === "string" && !siblings.some((entity) =>
                    entity !== editedEntity && entity.name.trim().toLowerCase() === value.trim().toLowerCase()),
                message: "That name already exists",
            }],
        }];

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: (progressCategory.entityAspect.entityState.isAdded() ? "Add" : "Edit")
                + " journey progress category within module"
                + ` "${progressCategory.progressProduct.name}"`,
            entities,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData);
    }
}
